if (!customElements.get('ctfa-video-hero')) {
  customElements.define(
    'ctfa-video-hero',
    class CtfaVideoHero extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.player = this.querySelector('[data-video-player]');
        if (!this.player) return;
        this.initialized = true;
        this.viewport = this.querySelector('[data-video-viewport]') || this;
        this.button = this.querySelector('[data-video-control]');
        this.label = this.querySelector('[data-video-control-label]');
        this.controls = this.querySelector('[data-video-controls]');
        this.status = this.querySelector('[data-video-status]');
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.userPaused = false;
        this.playToken = 0;
        this.videoListeners = [];
        // An above-the-fold hero need not wait for the first observer callback.
        const bounds = this.viewport.getBoundingClientRect();
        this.inView =
          !('IntersectionObserver' in window) ||
          (bounds.bottom > 0 && bounds.top < window.innerHeight && bounds.right > 0 && bounds.left < window.innerWidth);
        this.onToggle = () => this.togglePlayback();
        this.onMotionChange = () => {
          if (!this.video) return;
          this.video.loop = !this.motionQuery.matches;
          if (this.motionQuery.matches) this.pausePlayback();
          else this.maybeAutoplay();
        };
        this.onBreakpointChange = () => this.selectVideo();
        this.onVisibilityChange = () => {
          if (document.hidden) this.pausePlayback();
          else this.maybeAutoplay();
        };
        this.button?.addEventListener('click', this.onToggle);
        this.motionQuery.addEventListener('change', this.onMotionChange);
        this.mobileQuery.addEventListener('change', this.onBreakpointChange);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.selectVideo();

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              this.inView = entry.isIntersecting;
              if (this.inView) this.maybeAutoplay();
              else this.pausePlayback();
            },
            { threshold: 0.05 }
          );
          this.observer.observe(this.viewport);
        }
      }

      disconnectedCallback() {
        if (!this.initialized) return;
        this.pausePlayback();
        this.observer?.disconnect();
        this.button?.removeEventListener('click', this.onToggle);
        this.motionQuery.removeEventListener('change', this.onMotionChange);
        this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.removeVideoListeners();
        this.player.replaceChildren();
        this.video = null;
        this.activeTemplate = null;
        this.initialized = false;
      }

      removeVideoListeners() {
        this.videoListeners.forEach(([event, listener]) => this.video?.removeEventListener(event, listener));
        this.videoListeners = [];
      }

      selectVideo() {
        const template =
          (this.mobileQuery.matches && this.querySelector('[data-video-mobile]')) ||
          this.querySelector('[data-video-desktop]');
        if (!template || template === this.activeTemplate) return;
        this.pausePlayback();
        this.removeVideoListeners();
        const fragment = template.content.cloneNode(true);
        const video = fragment.querySelector('video');
        if (!video) return;
        this.video = video;
        this.activeTemplate = template;
        this.autoplayBlocked = false;
        this.mediaFailed = false;
        this.retryOnReady = false;
        this.readyRetries = 0;
        this.classList.remove('has-video-frame');
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = !this.motionQuery.matches;
        // Playback stays controlled so reduced motion and an explicit pause win.
        video.autoplay = false;
        video.preload = this.motionQuery.matches ? 'none' : this.inView ? 'auto' : 'metadata';
        video.setAttribute('aria-hidden', 'true');
        const listen = (event, listener) => {
          video.addEventListener(event, listener);
          this.videoListeners.push([event, listener]);
        };
        listen('playing', () => {
          if (video !== this.video) return;
          if (!this.wantsPlayback || document.hidden || !this.inView) {
            this.pausePlayback();
            return;
          }
          this.pendingPlay = false;
          this.retryOnReady = false;
          this.classList.add('has-video-frame');
          this.updateControl('playing');
        });
        listen('pause', () => {
          // A queued pause from an earlier request must not cancel a newer play.
          if (video !== this.video || !video.paused) return;
          this.playToken += 1;
          this.wantsPlayback = false;
          this.pendingPlay = false;
          this.retryOnReady = false;
          if (!this.mediaFailed && !this.autoplayBlocked) this.updateControl('paused');
        });
        listen('waiting', () => {
          if (video === this.video && this.wantsPlayback && !video.paused && !this.pendingPlay) {
            this.updateControl('buffering');
          }
        });
        listen('canplay', () => {
          // At most one readiness-triggered retry for an interrupted request.
          // Policy denials and unsupported media are never retried this way.
          if (video !== this.video || !this.retryOnReady || this.readyRetries >= 1) return;
          this.retryOnReady = false;
          this.readyRetries += 1;
          this.maybeAutoplay();
        });
        listen('ended', () => {
          if (video !== this.video) return;
          this.userPaused = true;
          this.pausePlayback();
        });
        listen('error', () => {
          if (video === this.video) this.showMediaError();
        });
        this.player.replaceChildren(fragment);
        this.updateControl('paused');
        this.maybeAutoplay();
      }

      maybeAutoplay() {
        if (
          this.video &&
          !this.video.error &&
          !this.mediaFailed &&
          this.inView &&
          !document.hidden &&
          !this.motionQuery.matches &&
          !this.userPaused &&
          !this.autoplayBlocked
        ) {
          this.startPlayback();
        }
      }

      startPlayback() {
        if (!this.video || this.mediaFailed || this.pendingPlay || this.wantsPlayback || document.hidden) return;
        const video = this.video;
        const token = ++this.playToken;
        this.wantsPlayback = true;
        this.pendingPlay = true;
        this.retryOnReady = false;
        video.preload = 'auto';
        video.loop = !this.motionQuery.matches;
        this.updateControl('loading');
        const rejected = (error) => {
          if (video !== this.video || token !== this.playToken) return;
          this.wantsPlayback = false;
          this.pendingPlay = false;
          if (error?.name === 'NotSupportedError' || video.error) {
            this.showMediaError();
          } else if (error?.name === 'NotAllowedError') {
            this.autoplayBlocked = true;
            this.updateControl('blocked');
          } else {
            this.retryOnReady = error?.name === 'AbortError' && this.readyRetries < 1;
            this.updateControl('paused');
          }
        };
        try {
          const attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') attempt.catch(rejected);
        } catch (error) {
          rejected(error);
        }
      }

      pausePlayback() {
        this.playToken += 1;
        this.wantsPlayback = false;
        this.pendingPlay = false;
        this.retryOnReady = false;
        if (this.video) this.video.pause();
        if (!this.mediaFailed) this.updateControl('paused');
      }

      showMediaError() {
        this.mediaFailed = true;
        this.pausePlayback();
        this.classList.remove('has-video-frame');
        this.updateControl('error');
      }

      togglePlayback() {
        if (!this.video || this.pendingPlay || this.mediaFailed) return;
        if (!this.video.paused) {
          this.userPaused = true;
          this.pausePlayback();
        } else {
          // Explicit play is allowed with reduced motion, but never loops.
          this.userPaused = false;
          this.autoplayBlocked = false;
          this.startPlayback();
        }
      }

      updateControl(state) {
        const loading = state === 'loading';
        const playing = state === 'playing' || state === 'buffering';
        const unavailable = state === 'error' || !this.video;
        this.dataset.playbackState = state;
        this.classList.toggle('is-playing', playing);
        if (this.controls) this.controls.hidden = unavailable;
        if (this.button) {
          this.button.hidden = unavailable;
          this.button.disabled = loading;
          this.button.dataset.state = state;
          this.button.setAttribute('aria-busy', String(loading));
        }
        if (this.label) this.label.textContent = loading ? 'Loading animation…' : playing ? 'Pause animation' : 'Play animation';
        if (this.status) {
          this.status.textContent = state === 'error'
            ? 'Background animation unavailable. The image is shown instead.'
            : state === 'blocked'
              ? 'Animation paused by your browser. Select Play animation to start.'
              : '';
        }
      }
    }
  );
}
